import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Cloud, Copy, CheckCircle2, XCircle, Loader2, ExternalLink, Unplug, ShieldCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CALLBACK_URL = `${window.location.origin}/api/salesforce/oauth/callback`;

const VERIFY_LABELS = {
  data_streams: "Data Streams", identity_resolution: "Identity Resolution Rulesets", segments: "Segments",
};

export default function SalesforceConnectPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ client_id: "", client_secret: "", org_type: "production" });
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const loadStatus = () => api.get("/salesforce/status").then((r) => setStatus(r.data));

  useEffect(() => {
    loadStatus();
    const s = params.get("status");
    if (s === "success") toast.success("Salesforce org connected successfully!");
    else if (s === "error") toast.error(`Connection failed: ${params.get("reason") || "unknown error"}`);
  }, [params]);

  const copyCallback = () => {
    navigator.clipboard.writeText(CALLBACK_URL).then(
      () => toast.success("Callback URL copied"),
      () => toast.error("Couldn't copy — please copy the URL manually")
    );
  };

  const connect = async (e) => {
    e.preventDefault();
    setConnecting(true);
    try {
      const { data } = await api.post("/salesforce/connect", form);
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start the connection");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    await api.post("/salesforce/disconnect");
    setStatus({ connected: false });
    setVerifyResult(null);
    toast.success("Disconnected from Salesforce org");
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/salesforce/verify");
      setVerifyResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setVerifying(false);
    }
  };

  if (!status) return <div className="min-h-screen flex items-center justify-center" data-testid="salesforce-connect-loading"><div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="salesforce-connect-page">
      <div className="flex items-center gap-3 mb-2">
        <Cloud className="w-6 h-6 text-sky-400" />
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-400">Real Org Verification</span>
      </div>
      <h1 className="font-heading text-3xl font-semibold text-white">Connect Your Salesforce Org</h1>
      <p className="text-sm text-slate-400 mt-2">Connect your own Salesforce Data Cloud org so we can verify — via read-only API calls — that you've actually set up Data Streams, Identity Resolution, and Segments for real, not just in the simulator.</p>

      {status.connected ? (
        <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="salesforce-connected-panel">
          <div className="flex items-center gap-2 text-emerald-400 mb-3">
            <ShieldCheck className="w-5 h-5" /> <span className="font-semibold">Connected</span>
          </div>
          <p className="text-sm text-slate-400">Org type: <span className="text-white">{status.org_type}</span></p>
          <p className="text-sm text-slate-400 mt-1">Instance: <span className="text-white break-all">{status.instance_url}</span></p>
          <p className="text-sm text-slate-400 mt-1">Connected: <span className="text-white">{new Date(status.connected_at).toLocaleString()}</span></p>

          <div className="flex items-center gap-3 mt-5">
            <button onClick={verify} disabled={verifying} data-testid="salesforce-verify-btn" className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Verify My Setup
            </button>
            <button onClick={disconnect} data-testid="salesforce-disconnect-btn" className="flex items-center gap-2 text-sm text-rose-400 hover:text-rose-300">
              <Unplug className="w-4 h-4" /> Disconnect
            </button>
          </div>

          {verifyResult && (
            <div className="mt-6 space-y-2.5" data-testid="salesforce-verify-results">
              {Object.entries(verifyResult).map(([key, r]) => (
                <div key={key} className="flex items-start gap-2.5" data-testid={`salesforce-verify-${key}`}>
                  {r.verified ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-sm text-white">{VERIFY_LABELS[key] || key} {r.verified && `(${r.count} found)`}</p>
                    {r.error && <p className="text-xs text-slate-500 mt-0.5">{r.error}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="glass-panel rounded-2xl p-6 mt-6" data-testid="salesforce-setup-instructions">
            <h3 className="text-sm font-bold text-white mb-3">1. Create a Connected App in your org</h3>
            <ol className="text-sm text-slate-400 space-y-1.5 list-decimal list-inside">
              <li>Setup → App Manager → New Connected App</li>
              <li>Enable OAuth Settings</li>
              <li>Set Callback URL to the value below</li>
              <li>Add scopes: "Manage user data via APIs (api)" and "Perform requests at any time (refresh_token, offline_access)"</li>
              <li>Save, then wait 2-10 minutes for Salesforce to propagate the app</li>
            </ol>
            <div className="flex items-center gap-2 mt-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
              <code className="text-xs text-indigo-300 flex-1 break-all" data-testid="salesforce-callback-url">{CALLBACK_URL}</code>
              <button onClick={copyCallback} data-testid="salesforce-copy-callback-btn" className="text-slate-400 hover:text-white shrink-0"><Copy className="w-4 h-4" /></button>
            </div>
          </div>

          <form onSubmit={connect} className="glass-panel rounded-2xl p-6 mt-6 space-y-3" data-testid="salesforce-connect-form">
            <h3 className="text-sm font-bold text-white mb-1">2. Enter your Connected App credentials</h3>
            <input value={form.client_id} onChange={(e) => setForm((f) => ({ ...f, client_id: e.target.value }))} placeholder="Consumer Key (Client ID)" required data-testid="salesforce-client-id-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <input type="password" value={form.client_secret} onChange={(e) => setForm((f) => ({ ...f, client_secret: e.target.value }))} placeholder="Consumer Secret (Client Secret)" required data-testid="salesforce-client-secret-input" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500" />
            <select value={form.org_type} onChange={(e) => setForm((f) => ({ ...f, org_type: e.target.value }))} data-testid="salesforce-org-type-select" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
              <option value="production">Production / Developer Edition</option>
              <option value="sandbox">Sandbox</option>
            </select>
            <button type="submit" disabled={connecting} data-testid="salesforce-connect-submit-btn" className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ExternalLink className="w-4 h-4" /> Connect to Salesforce</>}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
